import { useSelector } from 'react-redux';
import { Color, useThemeValue } from '@altermaind/web/ui';
import { SvgProps } from '@/_shared/types/svg';
import { getModeTheme } from '../styles/state/selectors';

type Props = SvgProps & {
  defaultWidth: string;
  defaultHeight: string;
  strokeAsPrimary?: boolean;
  defaultColorLightMode?: Color;
  defaultColorDarkMode?: Color;
};

/**
 * Custom hook to compute SVG style properties based on the current theme.
 *
 * @param {string} defaultWidth - **(Required)** Default width to use for the SVG element if not specified in `style`.
 *
 * @param {string} defaultHeight - **(Required)** Default height to use for the SVG element if not specified in `style`.
 *
 * @param {boolean} [strokeAsPrimary] - **(Optional)** If **`true`**, the **stroke** color is used as the primary color; otherwise, the **fill** color is primary.
 *
 * @param {Color} [defaultColorLightMode='primary/text'] - **(Optional)** Default color to use in light mode.
 *
 * @param {Color} [defaultColorDarkMode='white'] - **(Optional)** Default color to use in dark mode.
 *
 * @param {object} style - **(Required)** Style overrides for the SVG element. This object may include:
 *   - **fill**: Optional fill color.
 *   - **stroke**: Optional stroke color.
 *   - **width**: Optional override for the SVG's width.
 *   - **height**: Optional override for the SVG's height.
 *   - Any additional SVG style properties (captured as **otherGenericStyle**), such as `transform`, `margin`, etc.
 *     These extra properties are separated from the computed attributes (fill, stroke, width, height) to ensure they do not
 *     interfere with the rendering of the SVG image.
 *
 * @returns {object} An object containing:
 *   - **primaryColorToUse**: The computed primary color for the SVG (either the fill or stroke, depending on `strokeAsPrimary`).
 *   - **secondaryColorToUse**: The computed secondary color for the SVG.
 *   - **widthToUse**: The computed width for the SVG element.
 *   - **heightToUse**: The computed height for the SVG element.
 *   - **otherGenericStyle**: An object containing all additional SVG style properties (e.g. `transform`, `margin`, etc) that are
 *     passed along to the SVG element without overriding the computed `fill`, `stroke`, `width`, and `height` attributes.
 *
 * @example
 * import React from 'react';
 * import { useSvgParsedStyle } from './path/to/useSvgParsedStyle';
 *
 * const MySvgComponent = ({ style }) => {
 *   const {
 *     primaryColorToUse,
 *     secondaryColorToUse,
 *     widthToUse,
 *     heightToUse,
 *     otherGenericStyle,
 *   } = useSvgParsedStyle({
 *     style,
 *     defaultWidth: '24px',
 *     defaultHeight: '24px',
 *     strokeAsPrimary: true,
 *   });
 *
 *   return (
 *     <svg
 *       xmlns="http://www.w3.org/2000/svg"
 *       {...otherGenericStyle}
 *       width={widthToUse}
 *       height={heightToUse}
 *       viewBox="0 0 24 24"
 * 			 fill='none'
 *     >
 *       <circle cx="12" cy="12" r="10" stroke={primaryColorToUse} fill={secondaryColorToUse} />
 *     </svg>
 *   );
 * };
 *
 * export default MySvgComponent;
 */
export const useSvgParsedStyle = ({
  style,
  strokeAsPrimary,
  defaultWidth,
  defaultHeight,
  defaultColorLightMode = 'primary/text',
  defaultColorDarkMode = 'white',
}: Props) => {
  const mode = useSelector(getModeTheme());
  const color = useThemeValue(
    'colors',
    mode === 'dark' ? defaultColorDarkMode : defaultColorLightMode,
  );

  const {
    stroke = undefined,
    fill = undefined,
    width = undefined,
    height = undefined,
    ...otherGenericStyle
  } = style;

  const computedColor: { [key in 'fill' | 'stroke']: string | undefined } = {
    fill: fill || color,
    stroke: stroke || color,
  };

  const widthToUse = width || defaultWidth;
  const heightToUse = height || defaultHeight;

  return {
    primaryColorToUse: strokeAsPrimary ? computedColor.stroke : computedColor.fill,
    secondaryColorToUse: strokeAsPrimary ? computedColor.fill : computedColor.stroke,
    widthToUse,
    heightToUse,
    otherGenericStyle,
  };
};
